import React, { useMemo, useState, useEffect } from 'react';
import { Row, Col, Button } from 'reactstrap';
import ActivityGanttModal from './gantt_modal';

import ControldocDataTable from '../../controldoc/datatable';
import { riskLevelBadge, aasmStateBadge, activityProgressBar } from '../helper';

const routes = () => {
  return {
    default: {
      indexActivitiesPath: Routes.compliance_activities_path,
      showActivityPath: Routes.compliance_activity_path,
      editActivityPath: Routes.edit_compliance_activity_path,
    },
    portal: {
      indexActivitiesPath: Routes.portal_compliance_activities_path,
      showActivityPath: Routes.portal_compliance_activity_path,
    }
  }
}

const getRoutes = (props) => {
  const {
    configuration,
  } = props

  let context = 'default';

  if(configuration?.context === 'portal'){
    context = 'portal';
  }

  return routes()[context];
}

const ComplianceActivityTable = (props) => {
  const {
    complianceContext,
    configuration,
    data: {
      aasmStateOptions,
      customFields,
      recurrenceOptions,
      riskLevelOptions,
    },
  } = props;

  const {
    indexActivitiesPath,
    showActivityPath,
    editActivityPath,
  } = getRoutes(props);

  const defaultRequestParams = useMemo(() => {
    let requestParams = {
      compliance_context_id: complianceContext?.hashid
    }

    if(configuration?.context === 'portal'){
      requestParams = {
        ...requestParams,
        tenant: configuration?.tenant
      }
    }

    return requestParams;
  }, [configuration?.context]);

  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);

  useEffect(() => {
    if (tableColumns) {
      let visibleColumns = _.reject(tableColumns, {'hidden': true});

      setSelectedColumns(visibleColumns);
      setSelectedColumnKeys( _.map(visibleColumns, 'field') );
    }
  }, []);

  const onColumnSelectionChange = (e) => {
    const newSelectedColumnKeys = e.value;
    const filteredColumns = _.filter(tableColumns, column => { return _.includes(newSelectedColumnKeys, column.field) })

    setSelectedColumns(filteredColumns);
    setSelectedColumnKeys(newSelectedColumnKeys);
  };

  const onSelectionChange = selectedRecords => {
    setSelectedActivities(selectedRecords);
  }

  const tableColumnsDefault = [
    ...(complianceContext?.enabled_resource_config ? [{
      field: 'resource',
      header: I18n.t('activerecord.attributes.compliance/activity.resource'),
      body: (row) => row?.resource?.name || I18n.t('no_entry'),
      filter: {
        enabled: true,
        placeholder: 'Ingrese el nombre del recurso',
        filterrific: { search: 'search_by_resource_name' }
      }
    }] : []),
    {
      field: 'name',
      header: I18n.t('activerecord.attributes.compliance/activity.name'),
      filter: {
        enabled: true,
        placeholder: 'Ingrese el nombre de la actividad',
        filterrific: { search: 'search_by_name' }
      },
      sort: {
        enabled: true,
        filterrific: { sort: 'name' }
      }
    },
    {
      field: 'description',
      header: I18n.t('activerecord.attributes.compliance/activity.description'),
      body: (row) => _.truncate(row?.description, { length: 50 }) || I18n.t('no_entry'),
      hidden: true,
    },
    {
      field: 'recurrence',
      header: I18n.t('activerecord.attributes.compliance/activity.recurrence.one'),
      body: (row) => I18n.t(`activerecord.attributes.compliance/activity.recurrence.${row?.recurrence}` || I18n.t('no_entry') ),
      filter: {
        enabled: true,
        inputType: 'simple_select',
        placeholder: 'Selecciona una recurrencia',
        options: recurrenceOptions,
        filterrific: { search: 'by_recurrence' },
      },
    },
    {
      field: 'start_date',
      header: I18n.t('activerecord.attributes.compliance/activity.start_date'),
      body: (row) => moment(row?.start_date).format('DD-MM-YYYY') || I18n.t('no_entry'),
      filter: {
        enabled: true,
        placeholder: 'Ingrese una fecha de inicio',
        filterrific: { search: 'search_by_start_date' },
      },
    },
    {
      field: 'deadline',
      header: I18n.t('activerecord.attributes.compliance/activity.deadline'),
      body: (row) => moment(row?.deadline).format('DD-MM-YYYY') || I18n.t('no_entry'),
      filter: {
        enabled: true,
        placeholder: 'Ingrese una fecha de cumplimiento',
        filterrific: { search: 'search_by_deadline' },
      },
    },
    {
      field: 'uploader_groups',
      header: I18n.t('activerecord.attributes.compliance/activity.uploader_groups'),
      body: (row) => renderGroupList(row?.uploader_groups),
      hidden: true,
    },
    {
      field: 'reviewer_groups',
      header: I18n.t('activerecord.attributes.compliance/activity.reviewer_groups'),
      body: (row) => renderGroupList(row?.reviewer_groups),
      hidden: true,
    },
    {
      field: 'consultant_groups',
      header: I18n.t('activerecord.attributes.compliance/activity.consultant_groups'),
      body: (row) => renderGroupList(row?.consultant_groups),
      hidden: true,
    },
    {
      field: 'informer_groups',
      header: I18n.t('activerecord.attributes.compliance/activity.informer_groups'),
      body: (row) => renderGroupList(row?.informer_groups),
      hidden: true,
    },
    {
      field: 'count_evidences_pending_review',
      header: I18n.t('activerecord.attributes.compliance/activity.count_evidences_pending_review'),
      body: (row) => row?.count_evidences_pending_review,
      hidden: true
    },
    {
      field: 'aasm_state',
      header: I18n.t('activerecord.attributes.compliance/activity.aasm_state.one'),
      body: (row) => aasmStateBadge(row?.aasm_state, 'compliance/activity') || I18n.t('no_entry'),
      filter: {
        enabled: true,
        inputType: 'simple_select',
        placeholder: 'Selecciona un estado',
        options: aasmStateOptions,
        filterrific: { search: 'by_aasm_state' },
      },
      sort: {
        enabled: true,
        filterrific: { sort: 'aasm_state_' }
      }
    },
    {
      field: 'risk_level',
      header: I18n.t('activerecord.models.compliance/risk_level.one'),
      body: (row) => riskLevelBadge(row?.risk_level) || I18n.t('no_entry'),
      filter: {
        enabled: true,
        inputType: 'simple_select',
        placeholder: 'Selecciona un nivel de riesgo',
        options: riskLevelOptions,
        filterrific: { search: 'by_risk_level_id' }
      },
      sort: {
        enabled: true,
        filterrific: { sort: 'risk_level.value_' }
      }
    },
    {
      field: 'progress',
      header: I18n.t('activerecord.attributes.compliance/activity.progress'),
      body: (row) => activityProgressBar(row) || I18n.t('no_entry'),
      sort: {
        enabled: true,
        filterrific: { sort: 'progress' }
      }
    }
  ];

  const renderGroupList = (groups) => {
    if (_.isEmpty(groups)) { return I18n.t('no_entry'); }

    return (
      <ul className="pl-3">
        {_.map(groups, (group) => (
          <li key={group.id} className="small">{group.name}</li>
        ))}
      </ul>
    )
  }

  const tableColumnsCustomFields = useMemo(() => {
    return _.map(customFields, function (customField) {
      return {
        field: `custom_fields.${ customField.code }`,
        hidden: true,
        header: _.capitalize(customField.label),
        body: (row) => {
          return row.custom_fields[customField.code] || I18n.t('no_entry')
        },
        filter: {
          enabled: true,
          placeholder: `Ingrese ${ _.capitalize(customField.label) }`,
          filterrific: {
            search: `search_by_key_custom_fields`,
            searchKey: customField.code
          }
        },
        configuration: {
          isCustomField: true
        }
      }
    })
  }, [customFields])

  const tableColumnsActions = [
    {
      field: 'actions',
      header: I18n.t('actions.actions'),
      body: (row) =>
      <>
        {
          row.policy.show && (
            <Button color="primary" outline size="sm" className="ml-2" href={showActivityPath(row.hashid, { ...defaultRequestParams })}>
              {I18n.t('actions.show')}
            </Button>
          )
        }
        {
          row.policy.edit && (
            <Button color="info" outline size="sm" className="ml-2" href={editActivityPath(row.hashid, { ...defaultRequestParams })}>
              {I18n.t('actions.edit')}
            </Button>
          )
        }
        {
          row.policy.destroy && (
            <Button
              color="danger"
              size="sm"
              outline
              className="ml-2"
              href={showActivityPath(row.id, { ...defaultRequestParams })}
              data-method="delete"
              data-title={I18n.t('compliance.activities.destroy.confirm.title')}
              data-confirm={I18n.t('compliance.activities.destroy.confirm.message')}
              data-type="confirmation_code"
            >
              {I18n.t('actions.destroy')}
            </Button>
          )
        }
      </>
    }
  ];

  const tableColumns = useMemo(() => {
    let columns = _.concat(
      tableColumnsDefault,
      tableColumnsCustomFields,
      tableColumnsActions
    )

    return _.compact(columns)
  }, [ complianceContext?.enabled_resource_config ])

  return (
    <Row>
      <Col md="12">
        <ControldocDataTable
          columns={selectedColumns}
          configuration={{
            showDistributionListFilter: false,
            showGridlines: true,
            context: 'compliance',
            fetchRecords: {
              endpoint: indexActivitiesPath,
              keyName: 'activities',
              defaultRequestParams: defaultRequestParams
            },
            selectable: {
              enabled: true,
              mode: 'multiple',
              selection: selectedActivities,
              onSelectionChange: onSelectionChange
            },
            exportZip: {
              enabled: true,
              endpoint: indexActivitiesPath,
            },
            exportXlsx: {
              enabled: true,
              endpoint: indexActivitiesPath,
            },
            customAction: (records) => (
              <ActivityGanttModal activities={records} />
            ),
            columnSelectable: {
              enabled: true,
              columns: tableColumns,
              selection: selectedColumnKeys,
              onSelectionChange: onColumnSelectionChange,
            }
          }}
        />
      </Col>
    </Row>
  );
};

export default ComplianceActivityTable;
